// import components
import {
  Box,
  Divider,
  Tooltip,
  Typography,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  ControlledAutoComplete,
  ControlledCheckbox
} from 'components/shared/ControlledFields';
import CustomDialog from 'components/shared/Dialog';
import ControlledTimeField from 'components/shared/ControlledFields/ControlledTimeField';
import { StyledBoxFlexCenter } from 'components/shared/Box/Box.styles';
import {
  StyledRoundModalBox,
  StyledRoundModalButton,
  StyledSortedFieldsBox
} from './RoundModal.styles';

// import hooks
import { yupResolver } from '@hookform/resolvers/yup';
import { MouseEvent, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// import types
import { AskRoundSettingsResponse } from 'generated/index';
import { defaultRoundValues } from '../../AIMatch.consts';
import { RoundModalForm } from './RoundModal.types';

// import schema
import schema from './schema';

// import utils
import { hhmmToMinutes, minutesToHHmm } from '../../utils';
import { Icon } from '@sdb/planning-ui';

interface RoundModalProps {
  round?: { index: number; round: AskRoundSettingsResponse } | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (index: number, round: AskRoundSettingsResponse) => void;
  isEdit?: boolean;
}

const RoundModal = (props: RoundModalProps) => {
  const { isOpen, onClose, onSave, isEdit, round } = props;

  const { t } = useTranslation('common', { keyPrefix: 'aiMatchSettings' });

  const methods = useForm<RoundModalForm>({
    defaultValues: {
      ...defaultRoundValues,
      minutesBeforeStart: '00:00',
      responseActionType: null
    },
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const {
    formState: { isDirty, isValid },
    control,
    getValues,
    reset
  } = methods;

  const sortedFields = Object.entries(defaultRoundValues).sort(
    ([a], [b]) => t(`roundType.${a}`)?.localeCompare(t(`roundType.${b}`))
  );

  const saveRound = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const formValues = getValues();
    const minutes =
      (typeof formValues?.minutesBeforeStart === 'string' &&
        hhmmToMinutes(formValues?.minutesBeforeStart)) ||
      0;

    onSave(round!.index, {
      ...formValues,
      minutesBeforeStart: minutes,
      responseActionType: (formValues.responseActionType as any)?.value
    });
  };

  const getSettingByType = (type: keyof AskRoundSettingsResponse) => {
    switch (type) {
      case 'responseActionType':
        return (
          <StyledBoxFlexCenter key={type}>
            <ControlledAutoComplete
              name={type}
              label={t(`roundType.${type}`)}
              control={control}
              optionLabelProp="label"
              idProperty="value"
              disableClearable
              options={[
                { label: t('connect'), value: 'CONNECT' },
                { label: t('subscribe'), value: 'SUBSCRIBE' }
              ]}
              variant="outlined"
            />
            <Tooltip title={t(`roundType.${type}Tooltip`)} arrow>
              <Icon type="circle-info" color="primary" />
            </Tooltip>
          </StyledBoxFlexCenter>
        );

      case 'minutesBeforeStart':
        return (
          <StyledBoxFlexCenter key={type}>
            <ControlledTimeField
              name={type}
              label={t(`roundType.${type}`)}
              control={control}
              variant="outlined"
              size="small"
            />
            <Tooltip title={t(`roundType.${type}Tooltip`)} arrow>
              <Icon type="circle-info" color="primary" />
            </Tooltip>
          </StyledBoxFlexCenter>
        );
      case 'includeFunctionPreference1':
      case 'includeFunctionPreference2':
      case 'includeOwn':
      case 'includeShared':
      case 'includePreference1':
      case 'includePreference2':
      case 'includeFlex':
      case 'includeFlexWithPreference1':
      case 'includeFlexWithPreference2':
      case 'includeDeployableAsFlex':
      case 'includeDeployableAsFlexWithPreference1':
      case 'includeDeployableAsFlexWithPreference2':
        return (
          <Box key={type}>
            <ControlledCheckbox
              name={type}
              label={t(`roundType.${type}`)}
              control={control}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (round) {
      reset({
        ...round.round,
        minutesBeforeStart: minutesToHHmm(round.round.minutesBeforeStart),
        responseActionType: {
          label: t(round.round.responseActionType.toLowerCase()),
          value: round.round.responseActionType
        } as any
      });
    }
  }, [round, reset, t]);

  return (
    <CustomDialog
      title={
        isEdit ? t('editRoundNr', { number: round!.index + 1 }) : t('addRound')
      }
      open={isOpen}
      onHidden={onClose}
      onClose={onClose}
      sx={{
        '& .MuiFormControl-root': {
          width: '500px'
        }
      }}
    >
      <StyledRoundModalBox>
        {sortedFields
          .filter(([, value]) => typeof value !== 'boolean')
          .map(([key]) =>
            getSettingByType(key as keyof AskRoundSettingsResponse)
          )}

        <StyledSortedFieldsBox>
          <Typography>{t('function')}</Typography>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={true}
                  disabled={true}
                  sx={{ cursor: 'not-allowed' }}
                />
              }
              label={t('roundType.includeOwnFunction')}
            />
          </Box>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={true}
                  disabled={true}
                  sx={{ cursor: 'not-allowed' }}
                />
              }
              label={t('roundType.includeFunctionGroup')}
            />
          </Box>
          {sortedFields
            .filter(
              ([key, value]) =>
                key.match(/function/i) && typeof value === 'boolean'
            )
            .map(([key]) =>
              getSettingByType(key as keyof AskRoundSettingsResponse)
            )}
        </StyledSortedFieldsBox>
        <Divider />
        <StyledSortedFieldsBox>
          <Typography>{t('planUnit')}</Typography>
          {sortedFields
            .filter(
              ([key, value]) =>
                !key.match(/flex/i) &&
                !key.match(/function/i) &&
                typeof value === 'boolean'
            )
            .map(([key]) =>
              getSettingByType(key as keyof AskRoundSettingsResponse)
            )}
        </StyledSortedFieldsBox>
        <Divider />
        <StyledSortedFieldsBox>
          <Typography>{t('flex')}</Typography>
          {sortedFields
            .filter(
              ([key, value]) =>
                key.match(/flex/i) &&
                !key.match(/deployable/i) &&
                typeof value === 'boolean'
            )
            .sort(([a], [b]) => {
              return a.localeCompare(b);
            })
            .map(([key]) =>
              getSettingByType(key as keyof AskRoundSettingsResponse)
            )}
        </StyledSortedFieldsBox>
        <Divider />
        <StyledSortedFieldsBox>
          <Typography>{t('deployableAsFlex')}</Typography>
          {sortedFields
            .filter(
              ([key, value]) =>
                key.match(/deployable/i) && typeof value === 'boolean'
            )
            .sort(([a], [b]) => {
              return a.localeCompare(b);
            })
            .map(([key]) =>
              getSettingByType(key as keyof AskRoundSettingsResponse)
            )}
        </StyledSortedFieldsBox>
      </StyledRoundModalBox>
      <StyledRoundModalButton
        type="submit"
        variant="contained"
        color="primary"
        sx={{
          mt: 2,
          float: 'right'
        }}
        onClick={saveRound}
        disabled={!isDirty || !isValid}
      >
        {t('save')}
      </StyledRoundModalButton>
    </CustomDialog>
  );
};

export default RoundModal;
